import { useEffect, useRef, useState } from "react";
import { Video, FileText, Sparkles, RefreshCw, Trash2, Plus, ExternalLink, ChevronDown, ChevronRight, TrendingUp, ShieldAlert, Target, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const INTEREST = {
  hot: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  warm: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  cold: "bg-sky-500/15 text-sky-300 border-sky-500/30",
};

function Analysis({ a }) {
  if (a?.error) return <p className="text-xs text-rose-400">Analysis failed: {a.error}</p>;
  if (!a) return null;
  return (
    <div className="space-y-3" data-testid="call-analysis">
      {a.summary && <p className="text-xs text-slate-300">{a.summary}</p>}
      {(a.buying_signals?.length > 0) && (
        <div>
          <p className="text-[10px] font-mono-tech uppercase tracking-wider text-emerald-400 mb-1 flex items-center gap-1"><TrendingUp size={11} /> Buying signals</p>
          <ul className="list-disc list-inside space-y-0.5">
            {a.buying_signals.map((s, i) => <li key={i} className="text-xs text-slate-300">{s}</li>)}
          </ul>
        </div>
      )}
      {(a.objections?.length > 0) && (
        <div>
          <p className="text-[10px] font-mono-tech uppercase tracking-wider text-amber-400 mb-1 flex items-center gap-1"><ShieldAlert size={11} /> Objections & risks</p>
          <ul className="list-disc list-inside space-y-0.5">
            {a.objections.map((s, i) => <li key={i} className="text-xs text-slate-300">{s}</li>)}
          </ul>
        </div>
      )}
      {(a.angles?.length > 0) && (
        <div>
          <p className="text-[10px] font-mono-tech uppercase tracking-wider text-[#9cc6f0] mb-1.5 flex items-center gap-1"><Target size={11} /> Angles to close</p>
          <div className="space-y-1.5">
            {a.angles.map((ang, i) => (
              <div key={i} className="rounded-md bg-[#4690d9]/[0.07] border border-[#4690d9]/20 p-2">
                <p className="text-xs font-semibold text-white">{ang.offer}</p>
                {ang.rationale && <p className="text-[11px] text-slate-400 mt-0.5">{ang.rationale}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      {a.next_step && (
        <div className="flex items-start gap-1.5 pt-1">
          <ArrowRight size={13} className="text-emerald-400 mt-0.5 shrink-0" />
          <p className="text-xs text-slate-200"><span className="text-slate-500">Next step: </span>{a.next_step}</p>
        </div>
      )}
    </div>
  );
}

function CallItem({ call, prospectId, onChanged }) {
  const [openTx, setOpenTx] = useState(false);
  const [busy, setBusy] = useState(false);
  const pending = call.analysis_status === "pending";
  const level = call.analysis?.interest_level;

  const analyze = async () => {
    setBusy(true);
    try {
      await api.post(`/prospects/${prospectId}/calls/${call.id}/analyze`);
      onChanged();
      toast.success("Analyzing call…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not analyze");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/prospects/${prospectId}/calls/${call.id}`);
      onChanged();
      toast.success("Call removed");
    } catch { toast.error("Could not remove call"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-md bg-black/30 border border-white/[0.06] p-3" data-testid={`call-item-${call.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[9px] font-mono-tech uppercase px-1.5 py-0.5 rounded bg-white/5 text-slate-400 flex items-center gap-1">
            {call.source === "zoom" ? <Video size={10} /> : <FileText size={10} />} {call.source}
          </span>
          <span className="text-xs text-slate-500 font-mono-tech">
            {new Date(call.recorded_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
          </span>
          {level && <span className={`text-[9px] font-mono-tech uppercase px-1.5 py-0.5 rounded border ${INTEREST[level] || "text-slate-400 border-white/10"}`}>{level}</span>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {call.recording_url && (
            <a href={call.recording_url} target="_blank" rel="noreferrer" data-testid={`call-recording-link-${call.id}`} className="p-1.5 rounded text-[#9cc6f0] hover:bg-white/5" title="Open recording"><ExternalLink size={13} /></a>
          )}
          <button onClick={remove} disabled={busy} data-testid={`call-delete-${call.id}`} className="p-1.5 rounded text-slate-500 hover:text-rose-400 hover:bg-white/5 disabled:opacity-50" title="Remove call"><Trash2 size={13} /></button>
        </div>
      </div>

      {call.interest_reason || call.analysis?.interest_reason ? (
        <p className="text-[11px] text-slate-500 mb-2 italic">{call.analysis?.interest_reason}</p>
      ) : null}

      {pending ? (
        <p className="text-xs text-slate-400 flex items-center gap-1.5"><RefreshCw size={12} className="animate-spin" /> Analyzing the transcript…</p>
      ) : call.analysis ? (
        <Analysis a={call.analysis} />
      ) : call.transcript ? (
        <button onClick={analyze} disabled={busy} data-testid={`call-analyze-${call.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#4690d9]/40 text-[#9cc6f0] hover:bg-[#4690d9]/10 text-xs font-semibold disabled:opacity-50">
          <Sparkles size={12} /> Analyze transcript
        </button>
      ) : (
        <p className="text-xs text-slate-500">No transcript on this recording yet.</p>
      )}

      {call.analysis && !pending && (
        <button onClick={analyze} disabled={busy} data-testid={`call-reanalyze-${call.id}`} className="mt-2 text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 hover:text-[#9cc6f0] flex items-center gap-1">
          <RefreshCw size={10} /> Re-analyze
        </button>
      )}

      {call.transcript && (
        <div className="mt-2 border-t border-white/5 pt-2">
          <button onClick={() => setOpenTx(!openTx)} className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 hover:text-slate-300 flex items-center gap-1" data-testid={`call-transcript-toggle-${call.id}`}>
            {openTx ? <ChevronDown size={11} /> : <ChevronRight size={11} />} Transcript
          </button>
          {openTx && <p className="text-[11px] text-slate-400 mt-1.5 max-h-48 overflow-y-auto whitespace-pre-wrap">{call.transcript}</p>}
        </div>
      )}
    </div>
  );
}

export default function CallsPanel({ prospect, onRefresh }) {
  const [calls, setCalls] = useState(prospect.calls || []);
  const [adding, setAdding] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    setCalls(prospect.calls || []);
  }, [prospect.id]);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/prospects/${prospect.id}`);
      setCalls(data.calls || []);
      onRefresh && onRefresh(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const hasPending = (calls || []).some((c) => c.analysis_status === "pending");
    if (hasPending && !pollRef.current) {
      pollRef.current = setInterval(refresh, 4000);
    } else if (!hasPending && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [calls]);

  const submit = async () => {
    if (!transcript.trim() && !url.trim()) { toast.error("Paste a transcript or a recording link"); return; }
    setBusy(true);
    try {
      await api.post(`/prospects/${prospect.id}/calls`, { transcript, recording_url: url || null });
      setTranscript(""); setUrl(""); setAdding(false);
      await refresh();
      toast.success(transcript.trim() ? "Call added — analyzing…" : "Recording linked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add call");
    } finally { setBusy(false); }
  };

  const sorted = [...calls].sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at));

  return (
    <div className="glass-panel p-4 mb-4" data-testid="calls-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="eyebrow text-[10px] flex items-center gap-1.5"><Video size={12} /> Calls & analysis</p>
        <button onClick={() => setAdding(!adding)} data-testid="add-call-toggle" className="text-[10px] font-mono-tech uppercase tracking-wider text-[#9cc6f0] hover:text-white flex items-center gap-1">
          <Plus size={12} /> Add call
        </button>
      </div>

      {adding && (
        <div className="rounded-md bg-black/30 border border-white/10 p-3 mb-3 space-y-2" data-testid="add-call-form">
          <input
            data-testid="add-call-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Zoom recording link (optional)"
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9]"
          />
          <textarea
            data-testid="add-call-transcript"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            placeholder="Paste the call transcript here to get closing angles…"
            rows={5}
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9] resize-y"
          />
          <div className="flex gap-2">
            <button onClick={submit} disabled={busy} data-testid="add-call-submit" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-xs font-semibold disabled:opacity-50">
              <Sparkles size={12} /> Add & analyze
            </button>
            <button onClick={() => { setAdding(false); setTranscript(""); setUrl(""); }} className="px-3 py-1.5 rounded-full border border-white/15 text-slate-300 text-xs font-semibold">Cancel</button>
          </div>
        </div>
      )}

      {sorted.length === 0 && !adding && (
        <p className="text-xs text-slate-500">No calls yet. Zoom recordings auto-link by email, or add one manually.</p>
      )}

      <div className="space-y-2.5">
        {sorted.map((c) => <CallItem key={c.id} call={c} prospectId={prospect.id} onChanged={refresh} />)}
      </div>
    </div>
  );
}
